import React, { type FC, type ComponentType, useMemo } from 'react';
import { di } from 'react-magnetic-di';
import type { RequestCreateFormState } from 'state/persisted/request-create';
import { useFieldsOverrides } from './use-fields-overrides';
import { useInitializeFields } from './use-initalize-fields';
import type { RequestCreateFormWithHOCsProps } from 'view/request-create-form/types';

interface Props {
    RequestCreateForm: ComponentType<RequestCreateFormWithHOCsProps>;
    requestCreateFormProps: RequestCreateFormWithHOCsProps;
    formData: RequestCreateFormState;
}

export const EnabledFormDecorator = ({ RequestCreateForm, requestCreateFormProps, formData }: Props) => {
    di(useInitializeFields, useFieldsOverrides);

    useInitializeFields({ fields: formData.fields });
    const { fields } = useFieldsOverrides({ fields: formData.fields });

    const formDataWithOverrides = useMemo(() => {
        return {
            ...formData,
            fields,
        };
    }, [formData, fields]);

    return <RequestCreateForm {...requestCreateFormProps} formData={formDataWithOverrides} />;
};

/**
 * Built to wrap RequestCreateFrom with UI modifications overrides.
 * It's responsible for initialazing UIM data as well as overrides (without value).
 */
export const withUiModificationsFormDecorator = () => {
    return (RequestCreateForm: ComponentType<RequestCreateFormWithHOCsProps>) => {
        const FormDecorator: FC<RequestCreateFormWithHOCsProps> = (props) => {
            di(EnabledFormDecorator);

            const { formData, portalId, requestTypeId } = props;
            // Don't wrap with UIM if there are no required data.
            // TODO: add check for isUiModificationsEnabled and pass overriden data when it's true, otherwise fields
            // Check if when reparenting will happen formData avaliabilty and isUiModificationsEnabled, if an issue add
            // isUiModificationsEnabled in useFieldsOverrides on return.
            if (!formData || !portalId || !requestTypeId) {
                return <RequestCreateForm {...props} />;
            }

            return (
                <EnabledFormDecorator
                    RequestCreateForm={RequestCreateForm}
                    requestCreateFormProps={props}
                    formData={formData}
                />
            );
        };

        FormDecorator.displayName = `withUiModificationsFormDecorator(${RequestCreateForm.displayName || RequestCreateForm.name})`;
        return FormDecorator;
    };
};
